import React, { useState } from 'react';
import { api } from '../lib/api.js';
import Button from '../components/Button.js';
import Card from '../components/Card.js';

export default function PracticeMap() {
  const [downloadProgress, setDownloadProgress] = useState<number | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const handleDownload = async () => {
    setIsDownloading(true);
    setStatusMessage('Fetching download link...');
    setDownloadProgress(0);

    try {
      const response = await api.get('/api/map/download');
      const url = response.data.url;

      if (!url) {
        setStatusMessage('No practice map uploaded yet. Check back soon!');
        setDownloadProgress(null);
        return;
      }

      // Fetch the file as a blob to force download with correct filename
      setStatusMessage('Downloading map file...');
      const fileResponse = await fetch(url);
      const blob = await fileResponse.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = 'PVP_Academy_Practice_Map.zip';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);

      setDownloadProgress(100);
      setStatusMessage('Download started! Check your downloads folder.');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Download failed';
      console.error('Download failed:', err);
      setStatusMessage(`Download failed: ${message}`);
      setDownloadProgress(null);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="flex-1 min-h-screen bg-background text-on-surface">
      <div className="mb-8 select-none">
        <h1 className="font-headline-lg text-headline-lg text-on-surface mb-2">Practice Map</h1>
        <p className="text-on-surface-variant text-body-sm">
          Download our specialized singleplayer training map for offline combat drills.
        </p>
      </div>

      <div className="grid grid-cols-12 gap-gutter select-none">
        <div className="col-span-12 lg:col-span-7 space-y-6">

          {/* Map preview — clean dark placeholder */}
          <div className="aspect-video w-full rounded-lg overflow-hidden border border-white/10 relative bg-[#151515] flex items-center justify-center">
            <div className="text-center">
              <span className="text-6xl">🗺️</span>
              <p className="text-on-surface-variant text-[12px] mt-2">Practice Map Preview</p>
            </div>
            <div className="absolute top-4 left-4 px-3 py-1 bg-black/70 border border-white/10 rounded font-mono text-[11px] uppercase tracking-wider text-error font-bold">
              Latest Version
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="font-headline-sm text-body-lg font-semibold text-on-surface">
              Offline Drills & Target Arenas
            </h3>
            <p className="text-on-surface-variant text-body-sm leading-relaxed">
              PVP Academy's official training world is engineered to hone layout speeds, hotkey reflex
              actions, and aim accuracy. It comes preloaded with customizable testing rooms and
              customizable bot configurations.
            </p>
          </div>

          <Card className="p-gutter space-y-4">
            <div className="flex justify-between items-center flex-wrap gap-4">
              <div>
                <div className="font-bold text-on-surface text-body-sm">PVP_Academy_Practice_Map.zip</div>
                <div className="text-[11px] text-on-surface-variant">Includes active bot AI scripts</div>
              </div>

              <Button
                onClick={handleDownload}
                disabled={isDownloading}
                className="uppercase tracking-wider font-bold flex items-center gap-2 py-3.5"
              >
                {isDownloading ? 'Preparing...' : 'Download Map'}
                <span className="material-symbols-outlined text-[18px]">download</span>
              </Button>
            </div>

            {downloadProgress !== null && (
              <div className="space-y-2 pt-2">
                <div className="flex justify-between text-[11px] text-on-surface-variant font-mono">
                  <span>Download progress</span>
                  <span>{downloadProgress}%</span>
                </div>
                <div className="w-full h-1 bg-surface-container rounded-full overflow-hidden">
                  <div
                    className="h-full bg-error rounded-full transition-all duration-300"
                    style={{ width: `${downloadProgress}%` }}
                  />
                </div>
              </div>
            )}

            {statusMessage && (
              <div className={`text-[12px] italic pt-1 ${
                statusMessage.includes('failed') || statusMessage.includes('No practice')
                  ? 'text-error'
                  : 'text-on-surface-variant'
              }`}>
                {statusMessage}
              </div>
            )}
          </Card>
        </div>

        <Card className="col-span-12 lg:col-span-5 p-gutter h-fit space-y-6">
          <h3 className="font-label-caps text-label-caps text-on-surface-variant uppercase tracking-widest">
            Key Features Included
          </h3>
          <div className="space-y-4 text-body-sm text-on-surface-variant">
            <div className="flex items-start gap-3">
              <span className="material-symbols-outlined text-green-500 font-bold">check_circle</span>
              <div>
                <div className="text-on-surface font-semibold">Custom AI Training Bots</div>
                <p className="text-[12px] mt-0.5">Selectable difficulty states, simulating real player strafing angles.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="material-symbols-outlined text-green-500 font-bold">check_circle</span>
              <div>
                <div className="text-on-surface font-semibold">Aim-pop Accuracy Trials</div>
                <p className="text-[12px] mt-0.5">Time-based obsidian-crystal popping accuracy modules.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="material-symbols-outlined text-green-500 font-bold">check_circle</span>
              <div>
                <div className="text-on-surface font-semibold">Momentum & KB Trials</div>
                <p className="text-[12px] mt-0.5">Mace strike height testing meters and hit timing indicators.</p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}